import { Button, Dialog } from '@alifd/next';
import { Select, Space, Table, TableColumnsType, TableProps, Tabs } from 'antd';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import Webcam from 'react-webcam';
import Swal from 'sweetalert2';
import useSWR from 'swr';

import ezconImg from '@/../public/ezcon.png';
import LoadingSpin from '@/components/UI/LoadingSpin';
import client from '@/lib/feathers';
import type { Query } from '@/lib/sendMessage';
import sendNotification from '@/lib/sendMessage';
import type { RootState } from '@/redux/store';
import type { PostalRegistration } from '@/types/database/postalRegistration';
import type { User } from '@/types/database/users';

import styles from './index.module.scss';
import type { PostalItemsSwrResponse, PostalServiceQuery } from './types';
import { getDetailList, sendInfo } from './utils';

const PostalItemsTable: React.FC<{
  setIsDataLoading: React.Dispatch<React.SetStateAction<boolean>>;
  currentUser: User | null;
  searchType: 'barcode' | 'rfid' | 'household';
  searchValue: string;
}> = ({ setIsDataLoading, currentUser, searchType, searchValue }) => {
  const postalRegistrationService = client.service('postalRegistration');

  // UI state
  const [currentPage, setCurrentPage] = useState<'inProgress' | 'inHistory'>(
    'inProgress'
  );
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState<
    'receive' | 'detail' | 'webcam' | 'signBoard'
  >('receive');
  const [updateItems, setUpdateItems] = useState<PostalRegistration[]>([]);

  const [showDetailItem, setShowDetailItem] =
    useState<PostalRegistration | null>(null);
  const [videoDevices, setVideoDevices] = useState<InputDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(
    null
  );
  const [receiverPhoto, setReceiverPhoto] = useState<string | null>(null);
  const [receiverSign, setReceiverSign] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getPostalServiceQuery = (
    postalStatus: '未領取' | { $nin: ['未領取'] }
  ) => {
    const postalServiceQuery: PostalServiceQuery = {
      $sort: {
        createdTime: -1,
      },
      $limit: pageSize,
      $skip: (page - 1) * pageSize,
      status: postalStatus,
    };

    if (searchValue && searchType === 'household') {
      postalServiceQuery.household = searchValue;
    }

    if (
      currentUser &&
      currentUser.householdNumbers &&
      currentUser.householdNumbers.length !== 0 &&
      searchValue &&
      (searchType === 'rfid' || searchType === 'barcode')
    ) {
      postalServiceQuery.household = { $in: currentUser.householdNumbers };
    }

    return postalServiceQuery;
  };

  const getPostalItems = async (
    postalStatus: '未領取' | { $nin: ['未領取'] }
  ) => {
    const postalServiceQuery = getPostalServiceQuery(postalStatus);
    const { data, total }: { data: PostalRegistration[]; total: number } =
      await client.service('postalRegistration').find({
        query: postalServiceQuery,
      });

    const postalItems: PostalItemsSwrResponse['postalItems'] = data.map(
      (item) => ({
        ...item,
        key: item._id,
      })
    );

    return { postalItems, total };
  };

  const {
    data: inProgressDataResponse,
    mutate: updateInProgressData,
    isLoading: isInProgressDataLoading,
  } = useSWR<PostalItemsSwrResponse>(
    [getPostalServiceQuery('未領取'), currentPage, searchValue],
    () => getPostalItems('未領取')
  );

  const {
    data: inHistoryDataResponse,
    mutate: updateInHistoryData,
    isLoading: isInHistoryDataLoading,
  } = useSWR<PostalItemsSwrResponse>(
    [getPostalServiceQuery({ $nin: ['未領取'] }), currentPage, searchValue],
    () => getPostalItems({ $nin: ['未領取'] })
  );

  useEffect(() => {
    setPage(1);
  }, [searchValue]);

  const webcamRef = useRef<Webcam>(null);
  const signatureRef = useRef<SignatureCanvas>(null);

  const user = useSelector((state: RootState) => state.user);

  const handleShowDetail = useCallback((item: PostalRegistration) => {
    setShowDialog(true);
    setDialogType('detail');
    setShowDetailItem(item);
  }, []);

  const handleReturnItem = useCallback(
    async (id: string) => {
      const { isConfirmed } = await Swal.fire({
        title: '確定要退回此物品嗎？',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#209BFA',
        cancelButtonColor: '#999',
      });
      if (isConfirmed) {
        try {
          await postalRegistrationService.patch(id, {
            status: '退貨中',
            receiveTime: '尚未退貨',
          });
          updateInProgressData();
          updateInHistoryData();
          Swal.fire({
            title: '郵件包裹進入退貨程序',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
          });
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: '退貨失敗',
            icon: 'error',
            showConfirmButton: false,
            timer: 1500,
          });
        }
      }
    },
    [postalRegistrationService, updateInHistoryData, updateInProgressData]
  );

  const handleInfo = useCallback(
    async (id: string) => {
      const item = inProgressDataResponse?.postalItems.find(
        (item) => item._id === id
      );
      if (item) {
        try {
          await sendInfo(item);
          Swal.fire({
            title: '通知成功',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
          });
        } catch (error) {
          console.error(error);
          Swal.fire({
            title: '通知失敗',
            icon: 'error',
            showConfirmButton: false,
            timer: 1500,
          });
        }
      } else {
        Swal.fire({
          title: '找不到該物品',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
        });
      }
    },
    [inProgressDataResponse?.postalItems]
  );

  const handleMultiInfo = useCallback(async () => {
    try {
      await Promise.all(updateItems.map((item) => sendInfo(item)));
      Swal.fire({
        title: '通知成功',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: '通知失敗',
        icon: 'error',
        showConfirmButton: false,
        timer: 1500,
      });
    }
  }, [updateItems]);

  const handlePrepareOneReceive = useCallback((item: PostalRegistration) => {
    setUpdateItems([item]);
    setShowDialog(true);
    setDialogType('receive');
  }, []);

  const handlePrepareMultiReceive = useCallback(async () => {
    if (updateItems.length === 0) {
      Swal.fire({
        title: '請選擇項目',
        icon: 'warning',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }
    const isSelectingMultipleHouse =
      [...new Set(updateItems.map((item) => item.household))].length > 1;
    if (isSelectingMultipleHouse) {
      const { isConfirmed } = await Swal.fire({
        title: '您選擇的郵件包裹不屬於同一戶，是否繼續？',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#209BFA',
        cancelButtonColor: '#999',
      });
      if (!isConfirmed) {
        return;
      }
    }
    setShowDialog(true);
    setDialogType('receive');
  }, [updateItems]);

  const handleReceiveConfirm = useCallback(async () => {
    if (receiverPhoto || receiverSign) {
      await Promise.all(
        updateItems.map(async (item) => {
          const updatePostalItems = {
            status: '已領取',
            residentPhoto: receiverPhoto,
            canvas: receiverSign,
            receiveTime: moment().format('YYYY-MM-DD HH:mm:ss'),
          };
          await postalRegistrationService.patch(item._id, updatePostalItems);
        })
      );
      const receiverName = currentUser?.name || '住戶';
      const method = receiverSign ? '簽名' : '拍照';
      const adminName = user?.auth?.user?.name || '';
      updateItems.forEach((item) => {
        const message = `${receiverName}以${method}方式向管理員：${adminName}領取${item.household}${item.packageType}`;
        let query: Query;
        if (currentUser?._id) {
          query = {
            _id: currentUser._id,
            firebaseClass: 'postalRegistration',
          };
        } else {
          query = {
            householdNumbers: item.household,
            firebaseClass: 'postalRegistration',
          };
        }
        sendNotification({
          title: '郵件包裹領取通知',
          content: message,
          class: 'postalRegistration',
          redirect: '/mobile/postal/postalFrontDesk',
          query,
        });
      });
      updateInProgressData();
      updateInHistoryData();
      setShowDialog(false);
      setUpdateItems([]);
      setReceiverPhoto(null);
      setReceiverSign(null);
      Swal.fire({
        title: '領取成功',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });
    } else {
      Swal.fire({
        title: '請拍照或簽名',
        icon: 'warning',
        showConfirmButton: false,
        timer: 1500,
      });
      return;
    }
  }, [
    receiverPhoto,
    receiverSign,
    updateItems,
    currentUser?.name,
    currentUser?._id,
    user?.auth?.user?.name,
    updateInProgressData,
    updateInHistoryData,
    postalRegistrationService,
  ]);

  const handleCapture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setReceiverPhoto(imageSrc);
    }
  }, [webcamRef]);

  const handleClearSignature = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  }, [signatureRef]);

  const handleSaveSignature = useCallback(() => {
    if (signatureRef.current) {
      const imageSrc = signatureRef.current.getTrimmedCanvas().toDataURL();
      setReceiverSign(imageSrc);
      setDialogType('receive');
    }
  }, [signatureRef]);

  const handleCancelReceiveProcess = useCallback(() => {
    setShowDialog(false);
    setReceiverPhoto(null);
    setReceiverSign(null);
  }, []);

  const tableRowSelection: TableProps<PostalRegistration>['rowSelection'] =
    useMemo(
      () => ({
        type: 'checkbox',
        selectedRowKeys: updateItems.map((item) => item._id || Math.random()),
        onChange(_, selectedRows) {
          setUpdateItems(selectedRows);
        },
      }),
      [updateItems]
    );
  const columns = useMemo(() => {
    const columns: TableColumnsType<PostalRegistration> = [
      {
        title: '編號',
        dataIndex: 'plateNumber',
        key: 'plateNumber',
        sorter: (a, b) => a.plateNumber - b.plateNumber,
      },
      {
        title: '包裹類型',
        dataIndex: 'packageType',
        key: 'packageType',
        sorter: (a, b) => a.packageType.localeCompare(b.packageType),
      },
      {
        title: '戶號',
        dataIndex: 'household',
        key: 'household',
        sorter: (a, b) => a.household.localeCompare(b.household),
      },
      {
        title: '收件人',
        dataIndex: 'recipient',
        key: 'recipient',
        sorter: (a, b) => a.recipient.localeCompare(b.recipient),
      },
      {
        title: '擺放位置',
        dataIndex: 'placement',
        key: 'placement',
        sorter: (a, b) => a.placement.localeCompare(b.placement),
      },
      {
        title: '廠商',
        dataIndex: 'vendor',
        key: 'vendor',
        sorter: (a, b) => a.vendor.localeCompare(b.vendor),
      },
      {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: '登記時間',
        dataIndex: 'packagerRegistrationTime',
        sorter: (a, b) =>
          a.packagerRegistrationTime - b.packagerRegistrationTime,
        render: (value) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
      },
    ];
    const timeColumn: TableColumnsType<PostalRegistration>[0] = {
      dataIndex: 'receiveTime',
      key: 'receiveTime',
      sorter: (a, b) => a.receiveTime.localeCompare(b.receiveTime),
    };
    if (currentPage === 'inProgress') {
      timeColumn.title = '未領取時間';
    } else {
      timeColumn.title = '領取時間';
    }
    columns.push(timeColumn);
    columns.push({
      title: '操作',
      dataIndex: '_id',
      key: '_id',
      render: (id, postalItem) => {
        return (
          <Space>
            <Button onClick={() => handleShowDetail(postalItem)}>查看</Button>
            {currentPage === 'inProgress' && (
              <>
                <Button
                  type="secondary"
                  onClick={() => handlePrepareOneReceive(postalItem)}
                >
                  領取
                </Button>
                <Button onClick={() => handleReturnItem(id)}>退貨</Button>
                <Button type="primary" onClick={() => handleInfo(id)}>
                  通知住戶
                </Button>
              </>
            )}
          </Space>
        );
      },
    });
    return columns;
  }, [
    currentPage,
    handleInfo,
    handlePrepareOneReceive,
    handleReturnItem,
    handleShowDetail,
  ]);

  useEffect(() => {
    const getVideoDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput'
      );
      setVideoDevices(videoDevices);
      setSelectedVideoDevice(videoDevices?.[0]?.deviceId || null);
    };
    getVideoDevices();
  }, []);

  // useEffect(() => {
  //   setUpdateItems([]);
  //   const newData = data.map((data) => ({
  //     ...data,
  //     key: data._id,
  //   }));
  //   const inProgressData: typeof newData = [];
  //   const inHistoryData: typeof newData = [];
  //   newData.forEach((item) => {
  //     if (postalStatus.inProgress.includes(item.status)) {
  //       inProgressData.push(item);
  //     } else {
  //       inHistoryData.push(item);
  //     }
  //   });
  //   setInProgressDataSource(inProgressData);
  //   setInHistoryDataSource(inHistoryData);
  // }, [data, currentPage]);

  useEffect(() => {
    if (!isInProgressDataLoading && !isInHistoryDataLoading) {
      setIsDataLoading(false);
    }
  }, [isInHistoryDataLoading, isInProgressDataLoading, setIsDataLoading]);

  return isInProgressDataLoading || isInHistoryDataLoading ? (
    <LoadingSpin />
  ) : (
    <Space
      direction="vertical"
      style={{
        width: '100%',
      }}
    >
      <Tabs
        activeKey={currentPage}
        onChange={(tab) => {
          setCurrentPage(tab as 'inProgress' | 'inHistory');
          setPage(1);
          setUpdateItems([]);
        }}
        style={{
          width: '100%',
        }}
        items={[
          {
            key: 'inProgress',
            label: `未領取包裹(${
              inProgressDataResponse ? inProgressDataResponse.total : 0
            })`,
          },
          {
            key: 'inHistory',
            label: `歷史紀錄(${
              inHistoryDataResponse ? inHistoryDataResponse.total : 0
            })`,
          },
        ]}
      />
      {currentPage === 'inProgress' && (
        <Space>
          <Button type="primary" onClick={handleMultiInfo}>
            一鍵通知
          </Button>
          <Button type="secondary" onClick={handlePrepareMultiReceive}>
            一鍵領取
          </Button>
        </Space>
      )}
      {
        <Table
          bordered
          rowSelection={tableRowSelection}
          dataSource={
            currentPage === 'inProgress'
              ? inProgressDataResponse?.postalItems
              : inHistoryDataResponse?.postalItems
          }
          columns={columns}
          sortDirections={['descend', 'ascend', 'descend']}
          showSorterTooltip={false}
          style={{
            width: '100%',
          }}
          pagination={{
            current: page,
            pageSize,
            total:
              currentPage === 'inProgress' && inProgressDataResponse
                ? inProgressDataResponse.total
                : currentPage === 'inHistory' && inHistoryDataResponse
                ? inHistoryDataResponse.total
                : 0,
            onChange: (page, pageSize) => {
              setPage((prevPage) => (prevPage === page ? prevPage : page));
              setPageSize((prevPageSize) =>
                prevPageSize === pageSize ? prevPageSize : pageSize
              );
            },
          }}
        />
      }
      <Dialog
        // 根據內容改變
        title="包裹詳細資訊"
        visible={showDialog}
        onClose={handleCancelReceiveProcess}
        footer={false}
      >
        {dialogType === 'detail' && (
          <>
            {showDetailItem && (
              <div className={styles.detailContainer}>
                {getDetailList(showDetailItem).map((item) => (
                  <div key={item.label} className={styles.detail}>
                    <span>{item.label}</span>
                    <div>{item.content}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {dialogType !== 'detail' && (
          <div className={styles.dialogContainer}>
            {dialogType === 'receive' && (
              <div className={styles.receiveFunctionsContainer}>
                <div className={styles.receiveFuncContainer}>
                  <p className={styles.title}>領取人拍照</p>
                  <div className={styles.receiveFuncPreview}>
                    <div className={styles.centerContainer}>
                      <img src={receiverPhoto || ezconImg} />
                    </div>
                  </div>
                  <Button onClick={() => setDialogType('webcam')}>
                    開啟拍照
                  </Button>
                </div>
                <div className={styles.receiveFuncContainer}>
                  <p className={styles.title}>領取人簽名</p>
                  <div className={styles.receiveFuncPreview}>
                    <div className={styles.centerContainer}>
                      <img src={receiverSign || ezconImg} />
                    </div>
                  </div>
                  <Button onClick={() => setDialogType('signBoard')}>
                    開啟簽名版
                  </Button>
                </div>
                <div className={styles.receiveButtons}>
                  <Button
                    disabled={!(receiverPhoto || receiverSign)}
                    type="primary"
                    onClick={handleReceiveConfirm}
                  >
                    確定
                  </Button>
                  <Button onClick={handleCancelReceiveProcess}>取消</Button>
                </div>
              </div>
            )}
            {dialogType === 'webcam' && (
              <div className={styles.webcamFuncContainer}>
                <div className={styles.webcamSection}>
                  <p className={styles.title}>鏡頭</p>
                  <div className={styles.webcamContainer}>
                    <Webcam
                      className={styles.webcam}
                      audio={false}
                      mirrored
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        deviceId: selectedVideoDevice ?? undefined,
                        width: 640,
                        height: 640,
                      }}
                    />
                  </div>
                </div>
                <div className={styles.webcamSection}>
                  <p className={styles.title}>預覽</p>
                  {receiverPhoto && <img src={receiverPhoto} />}
                </div>
                <div className={styles.webcamButtons}>
                  <Select
                    value={selectedVideoDevice}
                    options={videoDevices.map((device) => ({
                      label: device.label,
                      value: device.deviceId,
                    }))}
                    placeholder="請選擇鏡頭"
                    style={{
                      width: 200,
                    }}
                  />
                  <Button type="primary" onClick={handleCapture}>
                    拍照
                  </Button>
                  <Button onClick={() => setDialogType('receive')}>確定</Button>
                </div>
              </div>
            )}
            {dialogType === 'signBoard' && (
              <div className={styles.signBoardFuncContainer}>
                <p className={styles.title}>簽名區</p>
                <div className={styles.signBoardContainer}>
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      width: 1000,
                      height: 550,
                    }}
                  />
                </div>
                <div className={styles.signBoardButtons}>
                  <Button onClick={handleClearSignature}>清除</Button>
                  <Button onClick={handleSaveSignature}>確定</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>
    </Space>
  );
};

export default PostalItemsTable;
